import { compareDates, daysInMonth, isSet, type PDate } from "../lib/dates";

/**
 * "When did Ana start at Day job?", asked for a job with a defined benefit pension (onboarding's
 * pension card and Update Data's "Retirement through work"): the year, and the month if known. Sent
 * as the job's start date (`IncomeSection.Income.start_date`, month 0 for a year only); the engine
 * stores a year only as 1 January and counts the pension's years of service from it. Blank: the
 * engine estimates it from the person's age (a "Sharpen your plan" estimate).
 */
export type JobStart = { year?: number; month?: number };

export function jobStartFrom(d: PDate | undefined): JobStart {
  return isSet(d) ? { year: d.year, month: d.month || undefined } : {};
}

/** The start date to send: the year, with the month when given; unset without a year. */
export function jobStartDate(s: JobStart): PDate | undefined {
  return s.year ? { year: s.year, month: s.month ?? 0, day: 0 } : undefined;
}

/**
 * The checks the engine makes on a start date answered here (`abi/merge/questionnaire.py`
 * `_check_job_starts`), at the answer's precision (a year only is the whole year): not after
 * `today` (a job with a DB pension only: `future` false skips it) and not before the 14th birthday.
 * `who`: "you", or the person's name. Whole sentences, shown beside the field.
 */
export function jobStartIssue(
  s: JobStart,
  dob: PDate | undefined,
  today: PDate,
  who: string,
  future = true,
): string | undefined {
  if (s.year === undefined) return s.month ? "Give the year too." : undefined;
  if (!Number.isInteger(s.year) || s.year < 1900) return "Give a year, such as 2012.";
  const first: PDate = { year: s.year, month: s.month || 1, day: 1 };
  const lastMonth = s.month || 12;
  const last: PDate = { year: s.year, month: lastMonth, day: daysInMonth(s.year, lastMonth) };
  if (future && compareDates(first, today) > 0)
    return `That’s after today. Give the year ${who} started there.`;
  if (isSet(dob) && compareDates(last, { ...dob, year: dob.year + 14 }) < 0)
    return `${who === "you" ? "You were" : `${who} was`} not yet 14 then. Check the year.`;
  return undefined;
}

/** The question, for a person ("you" or a name) and a job's name (blank: "this job"). */
export function jobStartQuestion(who: string, job: string): string {
  return `When did ${who} start ${job.trim() ? `at ${job.trim()}` : "this job"}?`;
}
